import { CheckCircle, BarChart3, Lock, RefreshCw, EyeOff } from 'lucide-react';
import type { Poll } from '@yawi/types';
import { useI18n } from '@/shared/i18n';
import { DetailedVotesTable } from './DetailedVotesTable';

interface PollResultsProps {
  poll: Poll;
  showVotedBadge?: boolean;
  showClosedBadge?: boolean;
  userVotedOptionIds?: string[];
  canChangeVote?: boolean;
  onChangeVote?: () => void;
}

export const PollResults: React.FC<PollResultsProps> = ({
  poll,
  showVotedBadge = false,
  showClosedBadge = false,
  userVotedOptionIds = [],
  canChangeVote = false,
  onChangeVote
}) => {
  const { t } = useI18n();
  const totalVotes = poll.options.reduce((sum, opt) => sum + opt.votes, 0);

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-6">
      {showVotedBadge && (
        <div className="flex items-center gap-2 text-emerald-600 mb-4">
          <CheckCircle size={18} />
          <span className="text-sm font-medium">{t('polls.voted')}</span>
        </div>
      )}
      {showClosedBadge && (
        <div className="flex items-center gap-2 text-slate-500 mb-4">
          <Lock size={18} />
          <span className="text-sm font-medium">{t('polls.closed')}</span>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-violet-100 text-violet-600">
          <BarChart3 size={20} />
        </div>
        <h3 className="text-lg font-bold text-slate-800">{poll.title}</h3>
      </div>

      {poll.description && (
        <p className="text-slate-500 text-sm mb-4">{poll.description}</p>
      )}

      <div className="space-y-3">
        {poll.options.map((option) => {
          const percentage = option.percentage != null
            ? option.percentage
            : totalVotes > 0 ? (option.votes / totalVotes) * 100 : 0;
          const isUserVote = userVotedOptionIds.includes(option.id);
          return (
            <div key={option.id} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className={`font-medium flex items-center gap-1.5 ${isUserVote ? 'text-violet-700' : 'text-slate-700'}`}>
                  {option.text}
                  {isUserVote && <CheckCircle size={14} className="text-violet-500" />}
                </span>
                <span className={isUserVote ? 'text-violet-600 font-medium' : 'text-slate-500'}>{percentage.toFixed(0)}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${isUserVote ? 'bg-violet-600' : 'bg-violet-400'}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-slate-500">
              {totalVotes} {t('polls.totalVotes')}
            </span>
            {poll.unitsVoted !== undefined && poll.totalUnits !== undefined && poll.totalUnits > 0 && (
              <span className="text-xs text-slate-400">
                {poll.unitsVoted} {t('polls.participation')} {poll.totalUnits} {t('polls.participationUnits')} ({Math.round((poll.unitsVoted / poll.totalUnits) * 100)}%)
              </span>
            )}
          </div>
          {canChangeVote && onChangeVote && (
            <button
              onClick={onChangeVote}
              className="text-sm text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1 transition-colors"
            >
              <RefreshCw size={14} />
              {t('polls.changeVote')}
            </button>
          )}
        </div>
      </div>

      {/* Detailed Votes or Anonymous indicator */}
      {poll.isAnonymous ? (
        <div className="flex items-center gap-2 text-xs text-slate-400 mt-3 pt-3 border-t border-slate-100">
          <EyeOff size={14} />
          <span>{t('polls.detailedVotes.anonymous')}</span>
        </div>
      ) : poll.detailedVotes && poll.detailedVotes.length > 0 ? (
        <DetailedVotesTable detailedVotes={poll.detailedVotes} />
      ) : null}
    </div>
  );
};
